import { getApiBaseUrlWithFallback } from "./apiBaseUrl";

export type NextFetchOptions = {
  revalidate?: number | false;
  tags?: string[];
};

export interface ApiRequestOptions extends RequestInit {
  parseJson?: boolean;
  next?: NextFetchOptions;
}

export interface ApiResult<T = unknown> {
  ok: boolean;
  status: number;
  data?: T;
}

import { authService } from "./authService";
import { guestAuthService } from "./guestAuthService";

export const apiFetch = async <T = unknown>(
  path: string,
  { parseJson = true, ...init }: ApiRequestOptions = {}
): Promise<ApiResult<T>> => {
  const { headers: customHeaders, ...restInit } = init;
  // Don't set Content-Type for FormData - browser will set it with boundary
  const isFormData = restInit.body instanceof FormData;
  // Real auth token takes priority; fall back to guest token for enrichment calls
  const token = authService.getToken() ?? guestAuthService.getToken();

  const headers: HeadersInit = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { "Authorization": `Bearer ${token}` } : {}),
    ...(customHeaders || {}),
  };

  const response = await fetch(`${getApiBaseUrlWithFallback()}${path}`, {
    cache: "no-store",
    credentials: "include",
    headers,
    ...restInit,
  });

  let data: T | undefined;
  if (parseJson) {
    try {
      const json = await response.json();
      data = json as T;

      // Auto-migrate session token from response if present
      // Handles both direct {token: ...} and wrapped {data: {token: ...}}
      const tokenFromResponse = (json as any)?.data?.token || (json as any)?.token;
      if (tokenFromResponse && typeof tokenFromResponse === "string") {
        authService.setToken(tokenFromResponse);
      }
    } catch {
      data = undefined;
    }
  }

  return {
    ok: response.ok,
    status: response.status,
    data,
  };
};
